import {useCallback, useState} from 'react';

import {useApiWithNotifications} from 'src/hooks/api-with-notification/useApiWithNotifications';
import {updateDraftConnection} from 'src/openapi/admin/apis/SsoV2Api';

import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {UpdateDraftConnectionRequest} from 'src/openapi/admin/models/UpdateDraftConnectionRequest';

type UseSaveDraftStepReturn = {
    saveDraft: (request: UpdateDraftConnectionRequest) => Promise<boolean>,
    saving: boolean,
};

const NOTIFICATION_MESSAGES = {
    success: 'SSO setup progress saved.',
    error: 'Failed to save SSO setup progress. Please try again.',
    exception: 'An unexpected error occurred while saving progress. Please try again.',
};

export function getSaveDraftErrorMessage(error?: ApiError): string | null {
    const detailMessages = error?.detailMessages
        ?.map(message => message.trim())
        .filter(Boolean) ?? [];

    return detailMessages.length > 0 ? detailMessages.join(', ') : null;
}

export function useSaveDraftStep(): UseSaveDraftStepReturn {
    const {executeWithNotifications} = useApiWithNotifications();
    const [saving, setSaving] = useState(false);

    const saveDraft = useCallback(async (request: UpdateDraftConnectionRequest): Promise<boolean> => {
        setSaving(true);
        try {
            const result = await executeWithNotifications<ApiError>(
                updateDraftConnection({updateDraftConnectionRequest: request}),
                NOTIFICATION_MESSAGES,
                'save-sso-draft',
                {
                    successTimeout: 3000,
                    getErrorMessage: getSaveDraftErrorMessage,
                },
            );
            return result.success;
        } finally {
            setSaving(false);
        }
    }, [executeWithNotifications]);

    return {saveDraft, saving};
}
